import { Box, Modal, SxProps, Theme, Typography } from '@mui/material';
import React, { CSSProperties, FC, ReactNode, useCallback, useState } from 'react';
import { Colors } from '../../../constants';
import { clsx } from '../../../utils';
import { CoreButtonBase, CoreIconButton } from '../core-button';
import { Showable } from '../../utils';
import { FadeSpinner } from '../fade-spinner';
import { CloseIcon } from '../../../icons';

export interface CoreConfirmationModalProps {
	open: boolean;
	icon?: FC<{ style?: CSSProperties }>;
	title?: ReactNode;
	message?: ReactNode;
	cancelText?: string;
	confirmText?: string;
	cancelSx?: SxProps<Theme>;
	confirmSx?: SxProps<Theme>;
	closeIconSx?: SxProps<Theme>;
	sx?: SxProps<Theme>;
	onConfirm?(): Promise<void> | void;
	onCancel?(): void;
}

export const CoreConfirmationModal: FC<CoreConfirmationModalProps> = props => {
	const {
		open,
		title,
		message,
		cancelText,
		confirmText,
		sx,
		cancelSx,
		confirmSx,
		closeIconSx,
		icon: Icon,
		onCancel,
		onConfirm
	} = props;
	const [isConfirming, setIsConfirming] = useState(false);

	const handleConfirm = useCallback(async (): Promise<void> => {
		setIsConfirming(true);
		await onConfirm?.();
		setIsConfirming(false);
	}, [onConfirm]);

	return (
		<Modal
			open={open}
			component='div'
			sx={{
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center',
				height: '100vh',
				background: 'rgba(22, 22, 22, 0.80)',
				backdropFilter: 'blur(12px)',
			}}
			onClose={() => {
				if (!isConfirming) {
					onCancel?.()
				}
			}}
		>
			<Box
				sx={clsx(
					{
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'space-between',
						position: 'relative',

						backgroundColor: Colors['1E1E1E'],
						border: `1px solid ${Colors['2A2A2A']}`,
						borderRadius: '8px',

						p: '24px 32px',
						gap: '24px',
					},
					sx
				)}
			>
				<CoreIconButton
					sx={clsx(
						{
							p: 0,
							width: '24px',
							height: '24px',
							color: Colors.white,

							position: 'absolute',
							top: '10px',
							right: '10px',

							"& >svg": {
								width: '18px',
								height: '18px',
							}
						},
						closeIconSx
					)}
					onClick={onCancel}
					disabled={isConfirming}
				>
					<CloseIcon width={24} height={24}/>
				</CoreIconButton>
				{Icon && (
					<Box
						sx={{
							alignSelf: 'center',
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							p: '20px',
							color: 'white',
							backgroundColor: Colors.white_a5,
							borderRadius: '50%',
							mb: '24px',
						}}
					>
						<Icon
							style={{
								width: '44px',
								height: '44px',
							}}
						/>
					</Box>
				)}

				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						textAlign: 'center',
						gap: '12px',
					}}
				>
					<Typography
						sx={{
							fontFamily: 'Montserrat, sans-serif',
							fontSize: '22px',
							fontWeight: 700,
							lineHeight: '140%',
							letterSpacing: '0.01em',
							color: Colors.white,
						}}
					>
						{title}
					</Typography>
					{message && (
						<Typography
							sx={{
								fontFamily: 'Montserrat, sans-serif',
								fontSize: '16px',
								fontWeight: 500,
								color: Colors.white_a80,
							}}
						>
							{message}
						</Typography>
					)}
				</Box>

				<Box
					sx={{
						display: 'flex',
						flexDirection: { xs: 'column', sm: 'row' },
						justifyContent: 'center',
						gap: '16px',
					}}
				>
					<CoreButtonBase
						disabled={isConfirming}
						variant='secondary'
						onClick={onCancel}
						sx={clsx({ p: '10px 24px' }, cancelSx)}
					>
						{cancelText || 'Cancel'}
					</CoreButtonBase>
					<CoreButtonBase
						disabled={isConfirming}
						onClick={handleConfirm}
						sx={clsx({ p: '10px 24px', gap: '6px' }, confirmSx)}
					>
						<Showable show={isConfirming}>
							<FadeSpinner
								size={15}
								height={4}
								width={2}
								borderRadius={1}
								color={Colors.black}
							/>
						</Showable>
						{confirmText || 'Confirm'}
					</CoreButtonBase>
				</Box>
			</Box>
		</Modal>
	);
};
